import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { backendUrl } from '../config/api';

axios.defaults.withCredentials = true;

const C = {
    bg:     '#060b1f',
    card:   'rgba(13,19,50,0.72)',
    border: 'rgba(255,255,255,0.08)',
    cyan:   '#00d4ff',
    green:  '#00c853',
    amber:  '#ffb300',
    red:    '#ff3b30',
    orange: '#ff6b00',
};
const COLOR_HEX = { red: '#ef4444', blue: '#3b82f6', green: '#22c55e', yellow: '#eab308' };

const SPEEDS = [
    { label: '0.5×', ms: 2000 },
    { label: '1×',   ms: 1000 },
    { label: '2×',   ms: 500  },
    { label: '5×',   ms: 200  },
    { label: '10×',  ms: 100  },
];

const diceEmoji = n => ['', '⚀', '⚁', '⚂', '⚃', '⚄', '⚅'][n] || n;

export default function ReplayPage() {
    const { roomId } = useParams();
    const navigate   = useNavigate();
    const { isAdmin, isApiManager, isLoggedIn } = useAuth();

    const [loading,  setLoading]  = useState(true);
    const [error,    setError]    = useState(null);
    const [room,     setRoom]     = useState(null);
    const [moveLog,  setMoveLog]  = useState([]);
    const [chatLog,  setChatLog]  = useState([]);
    const [pawns,    setPawns]    = useState([]); // current simulated state

    const [step,     setStep]     = useState(0);   // current move index being shown
    const [playing,  setPlaying]  = useState(false);
    const [speedIdx, setSpeedIdx] = useState(1);   // index into SPEEDS (default 1×)

    const intervalRef    = useRef(null);
    const initialPawns   = useRef([]); // stored from API response

    // Redirect non-privileged users
    useEffect(() => {
        if (!isLoggedIn || (!isAdmin && !isApiManager)) {
            navigate('/auth/login');
        }
    }, [isLoggedIn, isAdmin, isApiManager, navigate]);

    const fetchReplay = useCallback(async () => {
        setLoading(true); setError(null);
        try {
            const res = await axios.get(`${backendUrl}/api/rooms/${roomId}/replay`);
            const ip = res.data.initialPawns || [];
            initialPawns.current = ip;
            setRoom(res.data.room);
            setMoveLog(res.data.moveLog || []);
            setChatLog(res.data.chatLog || []);
            setPawns(ip);
            setStep(0);
        } catch (e) {
            setError(e.response?.data?.message || 'Failed to load replay data');
        } finally { setLoading(false); }
    }, [roomId]);

    useEffect(() => { fetchReplay(); }, [fetchReplay]);

    // Apply a single move log entry to the pawn state.
    const applyMove = useCallback((pawnsState, move) => {
        if (!move || move.pawnIdx === undefined || move.pawnIdx === null) return pawnsState;

        // Find killed pawn BEFORE moving attacker (check original pawnsState)
        let killedIdx = -1;
        if (move.killed) {
            killedIdx = pawnsState.findIndex(
                (p, i) => i !== move.pawnIdx && p.color !== move.color && p.position === move.toPos
            );
        }

        return pawnsState.map((p, i) => {
            if (i === move.pawnIdx) return { ...p, position: move.toPos };
            if (move.killed && i === killedIdx) return { ...p, position: p.basePos };
            return p;
        });
    }, []);

    // Rebuild state from scratch up to targetStep using stored initialPawns
    const goTo = useCallback((targetStep, allMoves) => {
        let state = [...initialPawns.current];
        const clamp = Math.min(targetStep, allMoves.length);
        for (let i = 0; i < clamp; i++) {
            state = applyMove(state, allMoves[i]);
        }
        setPawns(state);
        setStep(clamp);
    }, [applyMove]);

    // Auto-play
    useEffect(() => {
        if (playing) {
            const delay = SPEEDS[speedIdx].ms;
            intervalRef.current = setInterval(() => {
                setStep(s => {
                    if (s >= moveLog.length) { setPlaying(false); return s; }
                    setPawns(prev => applyMove(prev, moveLog[s]));
                    return s + 1;
                });
            }, delay);
        } else {
            clearInterval(intervalRef.current);
        }
        return () => clearInterval(intervalRef.current);
    }, [playing, speedIdx, moveLog, applyMove]);

    const currentMove = moveLog[step - 1]; // last applied move

    if (!isLoggedIn || (!isAdmin && !isApiManager)) return null;

    return (
        <div style={{
            minHeight: '100vh',
            background: `${C.bg} radial-gradient(ellipse 100% 40% at 50% 0%, rgba(0,212,255,0.06) 0%, transparent 60%)`,
            color: '#fff', fontFamily: 'system-ui, sans-serif',
        }}>
            {/* Header */}
            <header style={{
                position: 'sticky', top: 0, zIndex: 30,
                background: 'rgba(6,11,31,0.94)', backdropFilter: 'blur(20px)',
                borderBottom: `1px solid ${C.border}`,
                padding: '0 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 52,
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 20 }}>▶️</span>
                    <span style={{ fontWeight: 800, fontSize: 15, background: `linear-gradient(90deg,${C.cyan},#fff)`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>
                        Game Replay
                    </span>
                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.3)', background: 'rgba(255,107,0,0.1)', border: '1px solid rgba(255,107,0,0.25)', padding: '2px 8px', borderRadius: 20, fontWeight: 600 }}>
                        {isAdmin ? 'ADMIN' : 'API MANAGER'}
                    </span>
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', fontFamily: 'monospace' }}>{roomId}</span>
                    <Link to="/admincp/admin" style={{ padding: '5px 12px', borderRadius: 8, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.04)', color: 'rgba(255,255,255,0.5)', fontSize: 12, textDecoration: 'none' }}>← Admin</Link>
                </div>
            </header>

            <div style={{ maxWidth: 900, margin: '0 auto', padding: '28px 20px' }}>

                {error && (
                    <div style={{ padding: '14px 18px', borderRadius: 12, background: 'rgba(255,59,48,0.1)', border: '1px solid rgba(255,59,48,0.3)', color: C.red, marginBottom: 20, fontSize: 13 }}>
                        ⚠ {error}
                        <button onClick={fetchReplay} style={{ marginLeft: 12, padding: '3px 10px', borderRadius: 7, background: 'rgba(255,255,255,0.06)', border: `1px solid ${C.border}`, color: '#fff', fontSize: 12, cursor: 'pointer' }}>Retry</button>
                    </div>
                )}

                {loading ? (
                    <div style={{ textAlign: 'center', padding: '80px 0', color: 'rgba(255,255,255,0.4)' }}>
                        <div style={{ width: 36, height: 36, border: '2px solid rgba(0,212,255,0.2)', borderTop: `2px solid ${C.cyan}`, borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
                        Loading replay…
                    </div>
                ) : room && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

                        {/* Room info */}
                        <div style={{ padding: '18px 22px', borderRadius: 16, background: C.card, border: `1px solid ${C.border}` }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
                                <span style={{ fontSize: 28 }}>🎲</span>
                                <div>
                                    <div style={{ fontWeight: 800, fontSize: 17 }}>{room.name || 'Game Room'}</div>
                                    <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.4)', marginTop: 2 }}>
                                        {room.gameMode} · {room.maxPlayers}P · {new Date(room.createDate).toLocaleString()}
                                    </div>
                                </div>
                                {room.winner && (
                                    <div style={{ marginLeft: 'auto', padding: '6px 14px', borderRadius: 20, background: 'rgba(255,179,0,0.15)', border: '1px solid rgba(255,179,0,0.3)', color: C.amber, fontWeight: 800, fontSize: 13 }}>
                                        🏆 {room.winner} wins
                                    </div>
                                )}
                            </div>
                        </div>

                        {moveLog.length === 0 ? (
                            <div style={{ padding: '40px', textAlign: 'center', background: C.card, borderRadius: 16, border: `1px solid ${C.border}` }}>
                                <div style={{ fontSize: 36, opacity: 0.25, marginBottom: 14 }}>📋</div>
                                <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 14 }}>No move log available for this game.</div>
                                <div style={{ color: 'rgba(255,255,255,0.25)', fontSize: 12, marginTop: 6 }}>Move logging is recorded for games played after this feature was enabled.</div>
                            </div>
                        ) : (
                            <>
                                {/* ── Playback controls ── */}
                                <div style={{ padding: '16px 22px', borderRadius: 16, background: C.card, border: `1px solid ${C.border}` }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginBottom: 16 }}>
                                        {/* Rewind */}
                                        <button onClick={() => { setPlaying(false); goTo(0, moveLog); }}
                                            style={{ padding: '8px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 14, cursor: 'pointer' }}>
                                            ⏮
                                        </button>
                                        {/* Step back */}
                                        <button onClick={() => { setPlaying(false); if (step > 0) goTo(step - 1, moveLog); }}
                                            disabled={step === 0}
                                            style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.06)', color: step === 0 ? 'rgba(255,255,255,0.2)' : '#fff', fontSize: 14, cursor: step === 0 ? 'not-allowed' : 'pointer' }}>
                                            ◀
                                        </button>
                                        {/* Play/Pause */}
                                        <button onClick={() => setPlaying(p => !p)}
                                            style={{ padding: '10px 22px', borderRadius: 12, border: 'none', background: playing ? `rgba(255,59,48,0.8)` : `linear-gradient(135deg,${C.cyan},#0090e7)`, color: '#fff', fontWeight: 800, fontSize: 15, cursor: 'pointer', minWidth: 80 }}>
                                            {playing ? '⏸ Pause' : '▶ Play'}
                                        </button>
                                        {/* Step forward */}
                                        <button onClick={() => { setPlaying(false); if (step < moveLog.length) { setPawns(prev => applyMove(prev, moveLog[step])); setStep(s => s + 1); } }}
                                            disabled={step >= moveLog.length}
                                            style={{ padding: '8px 12px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.06)', color: step >= moveLog.length ? 'rgba(255,255,255,0.2)' : '#fff', fontSize: 14, cursor: step >= moveLog.length ? 'not-allowed' : 'pointer' }}>
                                            ▶
                                        </button>
                                        {/* Skip to end */}
                                        <button onClick={() => { setPlaying(false); goTo(moveLog.length, moveLog); }}
                                            style={{ padding: '8px 14px', borderRadius: 10, border: `1px solid ${C.border}`, background: 'rgba(255,255,255,0.06)', color: '#fff', fontSize: 14, cursor: 'pointer' }}>
                                            ⏭
                                        </button>

                                        {/* Speed selector */}
                                        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>Speed:</span>
                                            {SPEEDS.map((s, i) => (
                                                <button key={i} onClick={() => setSpeedIdx(i)}
                                                    style={{ padding: '5px 10px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer', border: `1px solid ${i === speedIdx ? C.cyan + '66' : C.border}`, background: i === speedIdx ? `rgba(0,212,255,0.15)` : 'rgba(255,255,255,0.04)', color: i === speedIdx ? C.cyan : 'rgba(255,255,255,0.5)' }}>
                                                    {s.label}
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    {/* Progress bar */}
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                        <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)', whiteSpace: 'nowrap' }}>Move {step} / {moveLog.length}</span>
                                        <div style={{ flex: 1, height: 6, borderRadius: 4, background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
                                            <div style={{ height: '100%', width: `${moveLog.length ? (step / moveLog.length) * 100 : 0}%`, background: `linear-gradient(90deg,${C.cyan},#0090e7)`, borderRadius: 4, transition: 'width 0.2s' }} />
                                        </div>
                                        <input type="range" min={0} max={moveLog.length} value={step}
                                            onChange={e => { setPlaying(false); goTo(Number(e.target.value), moveLog); }}
                                            style={{ width: 120 }} />
                                    </div>
                                </div>

                                {/* ── Current move info ── */}
                                {currentMove && (
                                    <div style={{ padding: '14px 20px', borderRadius: 14, background: C.card, border: `1px solid ${COLOR_HEX[currentMove.color] || C.border}44`, display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                                        <span style={{ width: 12, height: 12, borderRadius: '50%', background: COLOR_HEX[currentMove.color] || '#888', boxShadow: `0 0 8px ${COLOR_HEX[currentMove.color] || '#888'}` }} />
                                        <span style={{ fontWeight: 700, color: COLOR_HEX[currentMove.color] || '#fff', fontSize: 14 }}>{currentMove.player}</span>
                                        <span style={{ fontSize: 22 }}>{diceEmoji(currentMove.dice)}</span>
                                        <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.6)' }}>
                                            Pawn {currentMove.pawnIdx + 1}: pos {currentMove.fromPos} → {currentMove.toPos}
                                        </span>
                                        {currentMove.killed && <span style={{ padding: '2px 8px', borderRadius: 8, background: 'rgba(255,59,48,0.15)', border: '1px solid rgba(255,59,48,0.3)', color: C.red, fontSize: 11, fontWeight: 700 }}>⚔ Kill</span>}
                                        {currentMove.safe  && <span style={{ padding: '2px 8px', borderRadius: 8, background: 'rgba(0,200,83,0.12)', border: '1px solid rgba(0,200,83,0.25)', color: C.green, fontSize: 11, fontWeight: 700 }}>🛡 Safe</span>}
                                        {currentMove.home  && <span style={{ padding: '2px 8px', borderRadius: 8, background: 'rgba(255,179,0,0.15)', border: '1px solid rgba(255,179,0,0.3)', color: C.amber, fontSize: 11, fontWeight: 700 }}>🏠 Home!</span>}
                                    </div>
                                )}

                                {/* ── Pawn state board ── */}
                                <div style={{ padding: '18px 22px', borderRadius: 16, background: C.card, border: `1px solid ${C.border}` }}>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14 }}>
                                        Board State at Move {step}
                                    </div>
                                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(190px,1fr))', gap: 10 }}>
                                        {['red','blue','green','yellow'].map(color => {
                                            const colorPawns = pawns.filter(p => p.color === color);
                                            if (!colorPawns.length) return null;
                                            const clr = COLOR_HEX[color];
                                            const homeCount = colorPawns.filter(p => p.position >= 68).length;
                                            const isMoving = currentMove?.color === color;
                                            return (
                                                <div key={color} style={{ padding: '12px 14px', borderRadius: 12, background: isMoving ? `${clr}14` : `${clr}08`, border: `2px solid ${isMoving ? clr + '66' : clr + '33'}`, transition: 'all 0.3s' }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 7, marginBottom: 8 }}>
                                                        <span style={{ width: 10, height: 10, borderRadius: '50%', background: clr, boxShadow: isMoving ? `0 0 6px ${clr}` : 'none' }} />
                                                        <span style={{ fontSize: 12, fontWeight: 700, color: clr, textTransform: 'capitalize' }}>{color}</span>
                                                        <span style={{ marginLeft: 'auto', fontSize: 10, color: 'rgba(255,255,255,0.4)' }}>🏠 {homeCount}/4</span>
                                                    </div>
                                                    <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                                                        {colorPawns.map((p, i) => {
                                                            const isActive = isMoving && currentMove?.pawnIdx === pawns.indexOf(p);
                                                            return (
                                                                <span key={i} style={{
                                                                    fontSize: 10, padding: '3px 8px', borderRadius: 7,
                                                                    background: p.position >= 68 ? `${clr}30` : p.position === p.basePos ? 'rgba(255,255,255,0.06)' : `${clr}18`,
                                                                    color: p.position >= 68 ? clr : p.position === p.basePos ? 'rgba(255,255,255,0.3)' : 'rgba(255,255,255,0.8)',
                                                                    border: isActive ? `1px solid ${clr}` : '1px solid transparent',
                                                                    fontWeight: 600, transition: 'all 0.25s',
                                                                }}>
                                                                    {p.position >= 68 ? '🏠' : p.position === p.basePos ? 'Base' : `P${p.position}`}
                                                                </span>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>

                                {/* ── Move log list ── */}
                                <div style={{ padding: '18px 22px', borderRadius: 16, background: C.card, border: `1px solid ${C.border}`, maxHeight: 320, overflowY: 'auto' }}>
                                    <div style={{ fontSize: 10, fontWeight: 800, color: 'rgba(255,255,255,0.3)', textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 12 }}>
                                        Move Log ({moveLog.length} moves)
                                    </div>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                        {moveLog.map((m, idx) => {
                                            const clr = COLOR_HEX[m.color] || '#888';
                                            const isThis = idx === step - 1;
                                            const isFuture = idx >= step;
                                            return (
                                                <div key={idx}
                                                    onClick={() => { setPlaying(false); goTo(idx + 1, moveLog); }}
                                                    style={{
                                                        padding: '6px 12px', borderRadius: 8, cursor: 'pointer',
                                                        background: isThis ? `${clr}20` : isFuture ? 'transparent' : 'rgba(255,255,255,0.02)',
                                                        border: isThis ? `1px solid ${clr}44` : '1px solid transparent',
                                                        opacity: isFuture ? 0.4 : 1, transition: 'all 0.15s',
                                                        display: 'flex', alignItems: 'center', gap: 10,
                                                    }}>
                                                    <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.25)', minWidth: 30, textAlign: 'right' }}>#{idx + 1}</span>
                                                    <span style={{ width: 8, height: 8, borderRadius: '50%', background: clr, flexShrink: 0 }} />
                                                    <span style={{ fontSize: 11, color: clr, fontWeight: 600, minWidth: 55 }}>{m.player}</span>
                                                    <span style={{ fontSize: 14 }}>{diceEmoji(m.dice)}</span>
                                                    <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)' }}>
                                                        {m.fromPos} → {m.toPos}
                                                    </span>
                                                    <div style={{ marginLeft: 'auto', display: 'flex', gap: 4 }}>
                                                        {m.killed && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 5, background: 'rgba(255,59,48,0.15)', color: C.red, fontWeight: 700 }}>kill</span>}
                                                        {m.safe   && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 5, background: 'rgba(0,200,83,0.1)', color: C.green, fontWeight: 700 }}>safe</span>}
                                                        {m.home   && <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 5, background: 'rgba(255,179,0,0.12)', color: C.amber, fontWeight: 700 }}>home</span>}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            </>
                        )}

                        {chatLog.length > 0 && (
                            <div style={{ padding: '18px 22px', borderRadius: 16, background: C.card, border: `1px solid ${C.border}` }}>
                                <div style={{ fontWeight: 800, fontSize: 15, marginBottom: 12, color: C.cyan }}>Chat History</div>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, maxHeight: 260, overflowY: 'auto' }}>
                                    {chatLog.map((msg, idx) => (
                                        <div key={`${msg.ts || idx}-${idx}`} style={{ padding: '8px 10px', borderRadius: 10, background: 'rgba(255,255,255,0.05)', border: `1px solid ${C.border}` }}>
                                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, marginBottom: 4 }}>
                                                <span style={{ fontWeight: 700, color: msg.color && COLOR_HEX[msg.color] ? COLOR_HEX[msg.color] : '#fff' }}>{msg.name || 'Player'}</span>
                                                <span style={{ fontSize: 11, color: 'rgba(255,255,255,0.35)' }}>{msg.ts ? new Date(msg.ts).toLocaleString() : '—'}</span>
                                            </div>
                                            <div style={{ fontSize: 13, color: 'rgba(255,255,255,0.82)', lineHeight: 1.45 }}>{msg.text}</div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>
            <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
        </div>
    );
}
